import { useCallback, useEffect, useRef, useState } from 'react';
import getMyWithdrawalRequests from '../api/pension-plan-withdrawals/getMyWithdrawalRequests';
import getUserEppConfigForMode from '../api/users/getUserEppConfigForMode';
import { AdhesionMode } from '../api/users/getAdhesionDetails';
import {
	AnnuityState,
	WithdrawalReason,
	WithdrawalRequest,
	findAnnuityState,
	isAnnuityKind,
	findOpenRequestForPlan,
	findStoppingReason
} from '../api/pension-plan-withdrawals/interfaces/WithdrawalRequest';

export interface UseWithdrawalRequestsResult {
	/** Plan resuelto para este modo, o null si el usuario no tiene uno. */
	userEppConfigId: string | null;
	requests: WithdrawalRequest[];
	/** La solicitud viva de este plan: bloquea el botón en "Pendiente". */
	openRequest: WithdrawalRequest | null;
	/**
	 * Motivo por el que este plan ya no admite aportaciones, o null.
	 *
	 * Sólo cuenta una solicitud CONFIRMADA: mientras está pendiente, la
	 * plataforma se queda como está — pedir un rescate no es haberlo tramitado.
	 */
	activeStoppingReason: WithdrawalReason | null;
	/**
	 * Renta mensual vigente de este plan, o null si no hay ninguna.
	 *
	 * Se deriva de las solicitudes confirmadas: es lo que hace que la card de
	 * renta siga diciendo "activa" después de recargar.
	 */
	annuityState: AnnuityState | null;
	/**
	 * Hay una solicitud de RENTA viva para este plan, todavía sin tramitar.
	 *
	 * Sirve para no dar por hecha una gestión que hace una persona a mano: la
	 * card dice "pendiente" en vez de adelantar el resultado.
	 */
	pendingAnnuity: boolean;
	loading: boolean;
	error: string | null;
	refresh: () => Promise<void>;
}

/**
 * Estado de rescate del plan de un modo concreto, leído del backend.
 *
 * Resuelve el plan él mismo a partir del modo: si cada vista lo hiciera por su
 * cuenta, el mismo efecto acabaría copiado en las cuatro que lo necesitan — y
 * ya pasó, con una copia extra dentro de un `fetchData` que tumbaba la pantalla
 * entera cuando la llamada fallaba.
 *
 * Sustituye al scaffold de `localStorage` (`rescate_solicitud_<email>`): aquel
 * era por usuario y por navegador; esto es por plan y compartido con cuarteto,
 * que es donde el equipo resuelve las solicitudes.
 */
const useWithdrawalRequests = (
	token: string | null | undefined,
	mode: AdhesionMode,
	/**
	 * Plan que el usuario tiene elegido en el selector. Con dos planes del MISMO
	 * tipo, `mode` no nombra una fila: el backend resuelve por orden y el estado
	 * de rescate —y la solicitud que se cree desde aquí— caería en el plan más
	 * antiguo. Sin él se resuelve por modo, como siempre.
	 */
	selectedPlanId?: string | null
): UseWithdrawalRequestsResult => {
	const [userEppConfigId, setUserEppConfigId] = useState<string | null>(null);
	const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Cada lectura lleva su número. Si al volver ya no es la última —el usuario
	// cambió de plan mientras tanto—, su respuesta se descarta: aplicarla por orden
	// de LLEGADA dejaría en el modal el id del plan que ya no se ve, y la
	// solicitud de rescate se registraría en él.
	const latestRead = useRef(0);

	const refresh = useCallback(async () => {
		// Se numera ANTES de la salida temprana: perder el token también deja
		// caducada la lectura que estuviera en vuelo.
		const read = ++latestRead.current;
		if (!token || !mode) {
			// La lectura caducada ya no apagará el «cargando»: lo apaga esta.
			setLoading(false);
			return;
		}

		const isStale = () => read !== latestRead.current;

		setLoading(true);
		setError(null);

		try {
			// `Promise.resolve(...)` porque encadenar sobre el resultado sólo protege
			// si la función devuelve una promesa: si devuelve undefined, el `.catch`
			// reventaba y se llevaba por delante la carga de la pantalla.
			const configRes = await Promise.resolve(
				getUserEppConfigForMode(token, mode, selectedPlanId ?? undefined)
			);
			if (isStale()) return;
			const configId = configRes?.data?.id ?? null;
			setUserEppConfigId(configId);

			if (!configId) {
				setRequests([]);
				return;
			}

			const planRequests = await Promise.resolve(getMyWithdrawalRequests(token, configId));
			if (isStale()) return;
			setRequests(planRequests ?? []);
		} catch (caught) {
			if (isStale()) return;
			// Un fallo aquí no puede tumbar la vista: sin solicitudes se pinta el
			// estado por defecto, que es el de "nunca ha rescatado".
			setUserEppConfigId(null);
			setRequests([]);
			setError(caught instanceof Error ? caught.message : 'No se pudieron cargar los rescates');
		} finally {
			if (!isStale()) setLoading(false);
		}
	}, [token, mode, selectedPlanId]);

	useEffect(() => {
		// Otro plan, otro modo u otro token: lo anterior se olvida YA, no cuando llegue
		// la respuesta. Sin id, el modal no puede registrar nada; con el viejo,
		// registraría en el plan equivocado. Un `refresh()` manual —tras enviar
		// una solicitud— relee el mismo plan y no pasa por aquí.
		setUserEppConfigId(null);
		setRequests([]);
		refresh();
	}, [refresh]);

	const openRequest = userEppConfigId ? findOpenRequestForPlan(requests, userEppConfigId) : null;

	return {
		userEppConfigId,
		requests,
		openRequest,
		activeStoppingReason: findStoppingReason(requests),
		annuityState: userEppConfigId ? findAnnuityState(requests, userEppConfigId) : null,
		pendingAnnuity: !!openRequest && isAnnuityKind(openRequest.kind),
		loading,
		error,
		refresh
	};
};

export default useWithdrawalRequests;
